import type { I18nName } from './static';
import type { Locale } from '../i18n';
import { localizedName } from './static';

// Mirrors the backend wc2026 bundle (matches/wc2026_bundle.go). Match fields are
// snake_case as served; predictions and rules are camelCase.

export type Team = { code: string; name: I18nName; confederation: string; flag: string };

export type Stadium = {
  code: string;
  name: string;
  tournament_name?: string;
  city: string;
  country: string;
  capacity?: number;
  year_opened?: number;
  home_teams?: string[];
  poster?: string;
  poster_artist?: string;
  poster_artist_bio?: string;
  poster_style?: string;
  poster_description?: I18nName;
  curiosities?: Record<string, string[]>;
};

export type MatchStatus = 'upcoming' | 'live' | 'done' | 'cancelled';
export type Phase = 'group' | 'r32' | 'r16' | 'qf' | 'sf' | 'third' | 'final';

export type BundleMatch = {
  number: number;
  matchday: number;
  phase: Phase;
  group?: string;
  stadium: string;
  kickoff_utc: string;
  team_a?: string;
  team_b?: string;
  team_a_slot?: string;
  team_b_slot?: string;
  status: MatchStatus;
  score_a?: number;
  score_b?: number;
  // 90-minute full-time score — the score graded for points. Equals
  // score_a/score_b when there was no extra time. Knockouts that go to ET
  // differ. Penalties are score_*_pens.
  score_a_ft?: number;
  score_b_ft?: number;
  score_a_pens?: number;
  score_b_pens?: number;
  minute?: number;
};

export type Bundle = {
  schema: number;
  version: string;
  tournament: { code: string; name: I18nName; starts_at: string; ends_at: string };
  teams: Record<string, Team>;
  groups: Record<string, string[]>;
  stadiums: Record<string, Stadium>;
  matches: BundleMatch[];
};

export type Prediction = { matchNumber: number; scoreA: number; scoreB: number; boost: boolean };

export type Rules = {
  outcome: number;
  homeGoals: number;
  awayGoals: number;
  diff: number;
  exact: number;
  boostMultiplier: number;
  boostTokens: number;
};

export const DEFAULT_RULES: Rules = {
  outcome: 3,
  homeGoals: 1,
  awayGoals: 1,
  diff: 2,
  exact: 6,
  boostMultiplier: 2,
  boostTokens: 3,
};

export const KNOCKOUT_PHASES: Phase[] = ['r32', 'r16', 'qf', 'sf', 'third', 'final'];

export function isKnockout(m: BundleMatch): boolean {
  return m.phase !== 'group';
}
export function isLive(m: BundleMatch): boolean {
  return m.status === 'live';
}
export function isLocked(m: BundleMatch): boolean {
  return m.status === 'live' || m.status === 'done' || m.status === 'cancelled';
}

/** Display label for a side: resolved team name, else the slot placeholder. */
export function sideLabel(
  bundle: Bundle,
  code: string | undefined,
  slot: string | undefined,
  locale: Locale,
): string {
  if (code && bundle.teams[code]) return localizedName(bundle.teams[code].name, locale);
  return slot || 'TBD';
}

/**
 * The 90-minute full-time score [a, b] — what points are graded on. Falls
 * back to the final score (matches with no extra time, or older data). Null
 * until the match has a score.
 */
export function gradeScore(m: BundleMatch): [number, number] | null {
  const a = m.score_a_ft ?? m.score_a;
  const b = m.score_b_ft ?? m.score_b;
  if (a == null || b == null) return null;
  return [a, b];
}

/** True when the final result differs from the 90' score (goals in extra time). */
export function wentToExtraTime(m: BundleMatch): boolean {
  return (
    m.score_a_ft != null &&
    m.score_b_ft != null &&
    m.score_a != null &&
    m.score_b != null &&
    (m.score_a_ft !== m.score_a || m.score_b_ft !== m.score_b)
  );
}

/** True when the match was decided by a penalty shoot-out. */
export function wentToPens(m: BundleMatch): boolean {
  return m.score_a_pens != null && m.score_b_pens != null;
}

/**
 * Score one prediction against a finished match using additive rules
 * (ported from the app's LeagueRules application). Returns 0 for non-final
 * matches or missing picks. Boost multiplies the awarded total.
 *
 * Graded on the 90-minute full-time score (extra time and penalties are
 * excluded), matching the server.
 */
export function scorePrediction(m: BundleMatch, pick: Prediction | undefined, rules: Rules): number {
  if (!pick || m.status !== 'done') return 0;
  const grade = gradeScore(m);
  if (!grade) return 0;
  const [a, b] = grade;
  let pts = 0;
  const realOutcome = Math.sign(a - b);
  const pickOutcome = Math.sign(pick.scoreA - pick.scoreB);
  if (realOutcome === pickOutcome) pts += rules.outcome;
  if (pick.scoreA === a) pts += rules.homeGoals;
  if (pick.scoreB === b) pts += rules.awayGoals;
  if (a - b === pick.scoreA - pick.scoreB) pts += rules.diff;
  if (pick.scoreA === a && pick.scoreB === b) pts += rules.exact;
  if (pts > 0 && pick.boost) pts *= rules.boostMultiplier;
  return pts;
}
